import React, { useState } from 'react';
import styled from 'styled-components';
import { Package, Calendar, Search, Filter, Truck, CheckCircle, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const HistoryItem = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const ItemHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f3f4f6;
`;

const ItemTop = styled.div`
  display: flex;
  justify-content: between;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 15px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const TrackingNumber = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const OrderNumber = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 5px 0;
`;

const ProductName = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch(props.status) {
      case 'delivered': return '#dcfce7';
      case 'in-transit': return '#dbeafe';
      case 'preparing': return '#fef3c7';
      case 'cancelled': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'delivered': return '#15803d';
      case 'in-transit': return '#1d4ed8';
      case 'preparing': return '#d97706';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const ItemDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
`;

const DetailItem = styled.div``;

const DetailLabel = styled.p`
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 3px 0;
`;

const DetailValue = styled.p`
  font-size: 13px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const ItemActions = styled.div`
  padding: 15px 20px;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: ${props => props.primary ? '#3b82f6' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: ${props => props.primary ? 'none' : '1px solid #d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#f3f4f6'};
  }
`;

const DeliveryDate = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

function DeliveryHistory() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('3months');
  const [searchTerm, setSearchTerm] = useState('');

  // 더미 배송 내역 데이터
  const deliveryHistory = [
    {
      id: 1,
      trackingNumber: 'TK123456789',
      orderNumber: 'ORD-2024-001',
      productName: '프리미엄 무선 헤드폰',
      status: 'delivered',
      statusText: '배송완료',
      courier: 'CJ대한통운',
      orderDate: '2024-02-10',
      deliveryDate: '2024-02-12',
      recipient: '김철수',
      address: '서울시 강남구 테헤란로 123'
    },
    {
      id: 2,
      trackingNumber: 'TK987654321',
      orderNumber: 'ORD-2024-002',
      productName: '스마트워치 시리즈 9',
      status: 'in-transit',
      statusText: '배송중',
      courier: '한진택배',
      orderDate: '2024-02-14',
      deliveryDate: '예정: 2024-02-16',
      recipient: '이영희',
      address: '서울시 서초구 강남대로 456'
    },
    {
      id: 3,
      trackingNumber: 'TK456789123',
      orderNumber: 'ORD-2024-003',
      productName: '블루투스 이어폰',
      status: 'preparing',
      statusText: '상품준비중',
      courier: '로젠택배',
      orderDate: '2024-02-15',
      deliveryDate: '예정: 2024-02-17',
      recipient: '박민수',
      address: '경기도 성남시 분당구'
    },
    {
      id: 4,
      trackingNumber: 'TK789123456',
      orderNumber: 'ORD-2024-004',
      productName: '클래식 레더 자켓',
      status: 'delivered',
      statusText: '배송완료',
      courier: 'CJ대한통운',
      orderDate: '2024-02-01',
      deliveryDate: '2024-02-03',
      recipient: '최지현',
      address: '부산시 해운대구'
    },
    {
      id: 5,
      trackingNumber: 'TK321654987',
      orderNumber: 'ORD-2024-005',
      productName: '러닝화 에어맥스',
      status: 'cancelled',
      statusText: '배송취소',
      courier: '한진택배',
      orderDate: '2024-01-28',
      deliveryDate: '취소됨',
      recipient: '정수민',
      address: '대구시 중구'
    }
  ];

  const filteredHistory = deliveryHistory.filter(item => {
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      item.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch(status) {
      case 'delivered': return <CheckCircle size={14} />;
      case 'in-transit': return <Truck size={14} />;
      case 'preparing': return <Clock size={14} />;
      case 'cancelled': return <AlertTriangle size={14} />;
      default: return <Package size={14} />;
    }
  };

  const handleTrackingClick = (trackingNumber) => {
    // 실시간 추적 페이지로 이동
    window.location.href = `/delivery-tracking/status?tracking=${trackingNumber}`;
  };

  return (
    <ContentLayout 
      title="배송 내역"
      subtitle="지난 배송 내역을 조회하고 관리할 수 있습니다"
    >
      <Container>
        <FilterSection>
          <FilterRow>
            <FilterSelect 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">전체 상태</option>
              <option value="preparing">상품준비중</option>
              <option value="in-transit">배송중</option>
              <option value="delivered">배송완료</option>
              <option value="cancelled">배송취소</option>
            </FilterSelect>

            <FilterSelect 
              value={periodFilter} 
              onChange={(e) => setPeriodFilter(e.target.value)}
            >
              <option value="1month">최근 1개월</option>
              <option value="3months">최근 3개월</option>
              <option value="6months">최근 6개월</option>
              <option value="1year">최근 1년</option>
            </FilterSelect>

            <SearchInput
              type="text"
              placeholder="운송장번호, 주문번호, 상품명 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterRow>
        </FilterSection>

        {filteredHistory.length > 0 ? (
          <HistoryList>
            {filteredHistory.map(item => (
              <HistoryItem key={item.id}>
                <ItemHeader>
                  <ItemTop>
                    <ItemInfo>
                      <TrackingNumber>{item.trackingNumber}</TrackingNumber>
                      <OrderNumber>주문번호: {item.orderNumber}</OrderNumber>
                      <ProductName>{item.productName}</ProductName>
                    </ItemInfo>
                    <StatusBadge status={item.status}>
                      {getStatusIcon(item.status)}
                      {item.statusText}
                    </StatusBadge>
                  </ItemTop>
                  
                  <ItemDetails>
                    <DetailItem>
                      <DetailLabel>택배회사</DetailLabel>
                      <DetailValue>{item.courier}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>주문일</DetailLabel>
                      <DetailValue>{item.orderDate}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>수령인</DetailLabel>
                      <DetailValue>{item.recipient}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>배송지</DetailLabel>
                      <DetailValue>{item.address}</DetailValue>
                    </DetailItem>
                  </ItemDetails>
                </ItemHeader>

                <ItemActions>
                  <DeliveryDate>
                    {item.status === 'delivered' ? `배송완료: ${item.deliveryDate}` : item.deliveryDate}
                  </DeliveryDate>
                  <div style={{display: 'flex', gap: '8px'}}>
                    {item.status !== 'cancelled' && (
                      <ActionButton 
                        primary 
                        onClick={() => handleTrackingClick(item.trackingNumber)}
                      >
                        <Package size={12} />
                        추적하기
                      </ActionButton>
                    )}
                    <ActionButton>
                      <ChevronRight size={12} />
                      상세보기
                    </ActionButton>
                  </div>
                </ItemActions>
              </HistoryItem>
            ))}
          </HistoryList>
        ) : (
          <EmptyState>
            <EmptyIcon>
              <Package size={40} />
            </EmptyIcon>
            <h3 style={{margin: '0 0 10px 0', color: '#374151'}}>
              배송 내역이 없습니다
            </h3>
            <p style={{margin: 0, color: '#6b7280'}}>
              검색 조건을 변경하거나 새로운 주문을 해보세요
            </p>
          </EmptyState>
        )}
      </Container>
    </ContentLayout>
  );
}

export default DeliveryHistory;